/**
 * Maintenance API - Maintenance requests, inspections, and trips
 * @module api/maintenance
 */

import { apiRequest } from './core.js';

// =============================================================================
// MAINTENANCE REQUEST API
// =============================================================================

/**
 * Maintenance Request API - Work order management
 */
export const maintenanceAPI = {
  getAll: () => apiRequest('/maintenance_request?_limit=5000'),
  getById: (id) => apiRequest(`/maintenance_request/${id}`),
  create: (request) => apiRequest('/maintenance_request', {
    method: 'POST',
    body: JSON.stringify(request),
  }),
  update: (id, request) => apiRequest(`/maintenance_request/${id}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  }),
  delete: (id) => apiRequest(`/maintenance_request/${id}`, {
    method: 'DELETE',
  }),

  // V2 Workflow transitions
  approve: (id) => apiRequest(`/maintenance_request/${id}/approve`, { method: 'POST' }),
  reject: (id, reason) => apiRequest(`/maintenance_request/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  schedule: (id, data) => apiRequest(`/maintenance_request/${id}/schedule`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  start: (id) => apiRequest(`/maintenance_request/${id}/start`, { method: 'POST' }),
  complete: (id, data) => apiRequest(`/maintenance_request/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  cancel: (id, reason) => apiRequest(`/maintenance_request/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  // Operations (child rows)
  getOperations: (requestId) => apiRequest(`/maintenance_request/${requestId}/operations`),
  addOperation: (requestId, op) => apiRequest(`/maintenance_request/${requestId}/operations`, {
    method: 'POST',
    body: JSON.stringify(op),
  }),
  removeOperation: (requestId, opId) => apiRequest(`/maintenance_request/${requestId}/operations/${opId}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// MAINTENANCE CATALOG API
// =============================================================================

export const maintenanceCatalogAPI = {
  getAll: () => apiRequest('/maintenance_catalog?_limit=200&is_active=1&_sort=category_code,sort_order'),
};

// =============================================================================
// TRIP API
// =============================================================================

/**
 * Trip API - Vehicle trip records
 */
export const tripAPI = {
  getAll: () => apiRequest('/trip'),
  getById: (id) => apiRequest(`/trip/${id}`),
  create: (trip) => apiRequest('/trip', {
    method: 'POST',
    body: JSON.stringify(trip),
  }),
  update: (id, trip) => apiRequest(`/trip/${id}`, {
    method: 'PUT',
    body: JSON.stringify(trip),
  }),
  delete: (id) => apiRequest(`/trip/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// INSPECTION API
// =============================================================================

/**
 * Inspection API - Vehicle inspections (DVIR, pre-trip, etc.)
 */
export const inspectionAPI = {
  getAll: () => apiRequest('/inspection'),
  getById: (id) => apiRequest(`/inspection/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/inspection?vehicle_id=${vehicleId}`),
  getByDriver: (driverId) => apiRequest(`/inspection?driver_id=${driverId}`),
  getByStatus: (status) => apiRequest(`/inspection?status=${status}`),
  getByType: (type) => apiRequest(`/inspection?type=${type}`),
  create: (inspection) => apiRequest('/inspection', {
    method: 'POST',
    body: JSON.stringify(inspection),
  }),
  update: (id, inspection) => apiRequest(`/inspection/${id}`, {
    method: 'PUT',
    body: JSON.stringify(inspection),
  }),
  delete: (id) => apiRequest(`/inspection/${id}`, {
    method: 'DELETE',
  }),
};

/**
 * Inspection Item API - Individual inspection checklist items
 */
export const inspectionItemAPI = {
  getAll: () => apiRequest('/inspection_item'),
  getById: (id) => apiRequest(`/inspection_item/${id}`),
  getByInspection: (inspectionId) => apiRequest(`/inspection_item?inspection_id=${inspectionId}`),
  create: (item) => apiRequest('/inspection_item', {
    method: 'POST',
    body: JSON.stringify(item),
  }),
  update: (id, item) => apiRequest(`/inspection_item/${id}`, {
    method: 'PUT',
    body: JSON.stringify(item),
  }),
  delete: (id) => apiRequest(`/inspection_item/${id}`, {
    method: 'DELETE',
  }),
};
